const TURKISH_LOCALE = "tr-TR";
const ARABIC_LOCALE = "ar";
const DEFAULT_LOCALE = "en-US";
const ARABIC_DIACRITICS_REGEX = /[\u0610-\u061A\u0640\u064B-\u065F\u0670\u06D6-\u06ED]/gu;
const ARABIC_JOINABLE_PREFIX_TOKEN_REGEX = /^[وفبكل]$/u;

function containsTurkishOrthography(value: string): boolean {
  return /[çğıöşüÇĞİÖŞÜı]/u.test(String(value || ""));
}

function containsArabicOrthography(value: string): boolean {
  return /[\u0600-\u06FF]/u.test(String(value || ""));
}

export function resolveVocabularyLocale(languageCode?: string | null, sampleText?: string): string {
  const normalizedLanguage = String(languageCode || "").trim().toLowerCase();
  if (normalizedLanguage === "tr" || containsTurkishOrthography(String(sampleText || ""))) {
    return TURKISH_LOCALE;
  }
  if (normalizedLanguage === "ar" || containsArabicOrthography(String(sampleText || ""))) {
    return ARABIC_LOCALE;
  }
  return DEFAULT_LOCALE;
}

function normalizeArabicVocabularySurface(value: string): string {
  return value
    .normalize("NFC")
    .replace(ARABIC_DIACRITICS_REGEX, "")
    .replace(/[أإآٱ]/g, "ا");
}

function normalizeArabicTokenSpacing(value: string): string {
  const tokens = String(value || "").trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) {
    return "";
  }
  const merged: string[] = [];
  for (let index = 0; index < tokens.length; index += 1) {
    const token = tokens[index];
    if (ARABIC_JOINABLE_PREFIX_TOKEN_REGEX.test(token) && index + 1 < tokens.length) {
      tokens[index + 1] = `${token}${tokens[index + 1]}`;
      continue;
    }
    merged.push(token);
  }
  return merged.join(" ");
}

function normalizeVocabularySurface(value: string, languageCode?: string | null): string {
  const normalizedLanguage = String(languageCode || "").trim().toLowerCase();
  const locale = resolveVocabularyLocale(languageCode, value);
  const baseValue = String(value || "")
    .trim()
    .toLocaleLowerCase(locale)
    .replace(/[’`]/g, "'")
    .replace(/-/g, " ");
  const scriptAwareValue = normalizedLanguage === "ar"
    ? normalizeArabicVocabularySurface(baseValue)
    : baseValue.normalize("NFC");

  const normalizedSurface = scriptAwareValue
    .replace(/[^\p{L}\p{N}'\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();

  return normalizedLanguage === "ar"
    ? normalizeArabicTokenSpacing(normalizedSurface)
    : normalizedSurface;
}

export function normalizeVocabularyAnswer(value: string, languageCode?: string | null): string {
  return normalizeVocabularySurface(value, languageCode);
}

export function normalizeVocabularySpelling(value: string, languageCode?: string | null): string {
  return normalizeVocabularySurface(value, languageCode).replace(/\s+/g, "");
}

export function buildVocabularyLetterSequence(value: string, languageCode?: string | null): string[] {
  return Array.from(normalizeVocabularySpelling(value, languageCode));
}

/* eslint-disable no-bitwise -- Seed hashing and the PRNG intentionally use 32-bit arithmetic. */
function hashVocabularyShuffleSeed(value: string): number {
  let hash = 2166136261;
  for (const character of value) {
    hash ^= character.codePointAt(0) || 0;
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function createVocabularyShuffleRandom(seed: string): () => number {
  let state = hashVocabularyShuffleSeed(seed) || 0x6d2b79f5;
  return () => {
    state += 0x6d2b79f5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}
/* eslint-enable no-bitwise */

export function shuffleVocabularyLetterSequence(
  sequence: readonly string[],
  seed?: string,
): string[] {
  const shuffled = [...sequence];
  const random = seed ? createVocabularyShuffleRandom(seed) : Math.random;
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
  }

  if (shuffled.length > 1 && shuffled.every((character, index) => character === sequence[index])) {
    const distinctIndex = shuffled.findIndex((character) => character !== shuffled[0]);
    if (distinctIndex > 0) {
      [shuffled[0], shuffled[distinctIndex]] = [shuffled[distinctIndex], shuffled[0]];
    }
  }

  return shuffled;
}

export function buildScrambledVocabularyLetterSequence(
  value: string,
  languageCode?: string | null,
  seed?: string,
): string[] {
  return shuffleVocabularyLetterSequence(
    buildVocabularyLetterSequence(value, languageCode),
    seed,
  );
}

export function formatVocabularyLetterChip(char: string, languageCode?: string | null): string {
  return String(char || "").toLocaleUpperCase(resolveVocabularyLocale(languageCode, char));
}
